import { Component, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { LL } from 'i18n/i18n.service';
import { FitnessPlan, Plan_Workout } from '@cf-shared/data-model/fitness-plan';
import { CsCallablePage } from 'providers/nav-controller';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { DocWatcher } from 'services/database-wrapper';
import { UserDb } from 'services/user-db.service';
import { ExerciseDbService } from 'services/exercise-db.service';
import { ChangeWorkoutPageParams, ChangeWorkoutPageResult } from './change-workout-interface';

interface WorkoutOption {
  planReference: number;
  name: string;
  exerciseNames: string;
  isDefault: boolean;
}

@Component({
  selector: 'page-change-workout',
  templateUrl: 'change-workout-page.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: ModalPageMarker, useValue: new ModalPageMarker({ width: 480, height: 'auto' }) }]
})
export class ChangeWorkoutPage extends CsCallablePage<ChangeWorkoutPageParams, ChangeWorkoutPageResult> {
  protected LL = LL;

  workoutOptions: WorkoutOption[] = [];
  selectedPlanReference: number = 0;
  loading = true;

  private plan: FitnessPlan;
  private currentPlanReference: number;
  private originalPlanReference: number;
  private docWatcher: DocWatcher;

  constructor(
    private userDb: UserDb,
    private exerciseDb: ExerciseDbService,
    
    private cd: ChangeDetectorRef
  ) {
    super();
  }

  async ionViewWillEnter() {
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return;
        this.cd.markForCheck();
      });
    }

    this.currentPlanReference = this.navParams.data.currentPlanReference;
    this.originalPlanReference = this.navParams.data.originalPlanReference;
    this.selectedPlanReference = this.currentPlanReference;

    this.plan = await this.userDb.loadPlanAsync(this.docWatcher);

    await this.buildWorkoutOptions();
    this.loading = false;
    this.cd.markForCheck();
  }

  private async buildWorkoutOptions(): Promise<void> {
    const options: WorkoutOption[] = [];
    for (let i = 0; i < this.plan.workouts.length; i++) {
      const workout = this.plan.workouts[i];
      const exerciseNames = await this.getExerciseNamesForWorkout(workout);
      options.push({
        planReference: i,
        name: LL.training.trainingProgram.workoutLetter({ letter: workout.name }),
        exerciseNames: exerciseNames.join(', '),
        isDefault: i === this.originalPlanReference
      });
    }
    this.workoutOptions = options;
  }

  private async getExerciseNamesForWorkout(workout: Plan_Workout): Promise<string[]> {
    const names: string[] = [];
    for (const subSlotId of workout.subSlotIds) {
      const subSlot = this.plan.subSlots[subSlotId];
      if (subSlot?.exercise_id) {
        const name = await this.exerciseDb.getExerciseNameByIdAsync(subSlot.exercise_id, this.docWatcher);
        names.push(name);
      }
    }
    return names;
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
  }

  cancel(event: Event) {
    lockClick(event, this.returnAsync(undefined));
  }

  save() {
    void this.returnAsync({ newPlanReference: this.selectedPlanReference });
  }
}
